import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { IconChartCohort, IconLayoutSidebarLeftCollapse } from '@tabler/icons-react';
import './BoardTechnicianViewModal.css';

const STACK_OPTIONS = [1, 2, 3];

/**
 * Modal de visualização do Board. Fecha só no X ou Cancelar.
 */
export function BoardTechnicianViewModal({
  open,
  onClose,
  onApply,
  technicianCount = 0,
  currentStackSize = 1,
  openColumnCollapsed = false,
}) {
  const [stackSize, setStackSize] = useState(1);
  const [minimizeOpen, setMinimizeOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStackSize(Math.max(1, Math.min(3, Number(currentStackSize) || 1)));
    setMinimizeOpen(Boolean(openColumnCollapsed));
  }, [open, currentStackSize, openColumnCollapsed]);

  if (!open) return null;

  const canPick = (size) => size <= 1 || technicianCount >= size;

  const handleApply = () => {
    onApply?.({
      stackSize: canPick(stackSize) ? stackSize : 1,
      minimizeOpenColumn: minimizeOpen,
    });
    onClose?.();
  };

  const modal = (
    <div className="btv-modal-overlay" role="presentation">
      <div
        className="btv-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="btv-modal-title"
      >
        <div className="btv-modal-header">
          <span className="btv-modal-header-icon" aria-hidden>
            <IconChartCohort size={18} stroke={2} />
          </span>
          <h2 id="btv-modal-title">Visualização técnica</h2>
          <button type="button" className="btv-modal-close" onClick={onClose} aria-label="Fechar">
            <X size={16} strokeWidth={2.2} />
          </button>
        </div>

        <div className="btv-modal-body">
          <section className="btv-modal-section">
            <div className="btv-modal-section-head">
              <span className="btv-modal-kicker">Colunas técnicas</span>
              <p>Empilhe técnicos na mesma faixa, de cima para baixo.</p>
            </div>
            <div className="btv-stack-options" role="radiogroup" aria-label="Empilhamento">
              {STACK_OPTIONS.map((size) => {
                const disabled = !canPick(size);
                const selected = stackSize === size;
                return (
                  <button
                    key={size}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={disabled}
                    className={[
                      'btv-stack-card',
                      selected ? 'is-selected' : '',
                      disabled ? 'is-disabled' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      if (!disabled) setStackSize(size);
                    }}
                  >
                    <span className="btv-stack-preview" aria-hidden>
                      {Array.from({ length: size }, (_, i) => (
                        <span key={i} className="btv-stack-cell" />
                      ))}
                    </span>
                    <span className="btv-stack-card-label">
                      {size === 1 ? '1 coluna' : `${size} empilhadas`}
                    </span>
                    <span className="btv-stack-card-hint">1 × {size}</span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="btv-modal-section">
            <div className="btv-modal-section-head">
              <span className="btv-modal-kicker">Em aberto</span>
              <p>Recolhe a coluna fixa e mantém este padrão na próxima visita.</p>
            </div>
            <button
              type="button"
              className={`btv-open-toggle${minimizeOpen ? ' is-on' : ''}`}
              aria-pressed={minimizeOpen}
              onClick={() => setMinimizeOpen((prev) => !prev)}
            >
              <span className="btv-open-toggle-preview" aria-hidden>
                <span className={`btv-open-mini${minimizeOpen ? ' is-collapsed' : ''}`}>
                  <span className="btv-open-mini-bar" />
                  {!minimizeOpen ? <span className="btv-open-mini-body" /> : null}
                </span>
              </span>
              <span className="btv-open-toggle-copy">
                <span className="btv-open-toggle-title">
                  <IconLayoutSidebarLeftCollapse size={16} stroke={2} aria-hidden />
                  Minimizar coluna em aberto
                </span>
                <span className="btv-open-toggle-desc">
                  {minimizeOpen ? 'Coluna recolhida (padrão)' : 'Coluna expandida'}
                </span>
              </span>
              <span className="btv-open-switch" aria-hidden>
                <span className="btv-open-switch-knob" />
              </span>
            </button>
          </section>
        </div>

        <div className="btv-modal-footer">
          <button type="button" className="btv-modal-btn btv-modal-btn--ghost" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btv-modal-btn btv-modal-btn--apply" onClick={handleApply}>
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}

export default BoardTechnicianViewModal;
